import { NoticesStagesv2 } from "lib/types/graphQLEnums";
import { CoreNotice } from "./types";

type NoticeData = {
  procurementStageId: string;
  publishDate: string | null;
  buyers: {
    guid: string;
    name: string;
    createdAt: string | null;
  }[];
};

// Sorts notices and buyers to make buyer selection more deterministic
// This is temporary until we have proper buyer selection UI in place
// The sorting should match the backend logic in resources/buyers/buyerActivity/loadNoticeBuyerData.ts
export function getNoticeBuyerData(notices: NoticeData[]) {
  if (notices.length === 0) return null;

  // Sort notices from latest to earliest publish date, with procurement stage ID as tiebreaker
  const sortedNotices = [...notices].sort((a, b) => {
    const dateDiff =
      new Date(b.publishDate || 0).getTime() - new Date(a.publishDate || 0).getTime();
    return dateDiff !== 0 ? dateDiff : a.procurementStageId.localeCompare(b.procurementStageId);
  });

  const firstNotice = sortedNotices[0];
  if (!firstNotice?.buyers?.length) return null;

  // Sort the first notice's buyers from latest to earliest created at date, with buyer guid as tiebreaker
  const sortedBuyers = [...firstNotice.buyers].sort((a, b) => {
    const dateDiff = new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
    return dateDiff !== 0 ? dateDiff : a.guid.localeCompare(b.guid);
  });

  return {
    notices: sortedNotices,
    buyer: sortedBuyers[0],
  };
}

export function getNoticeDateInfo(notice: CoreNotice): { label?: string; date?: string | null } {
  const now = new Date();

  switch (notice.stagev2) {
    case NoticesStagesv2.AwardedContract:
    case NoticesStagesv2.ExpiredContract:
    case NoticesStagesv2.CancelledAward: {
      const date = notice.expiryDate;
      const isFutureDate = date ? new Date(date) > now : false;
      return {
        label: isFutureDate ? "Expires" : "Expired",
        date,
      };
    }

    case NoticesStagesv2.OpenTender:
    case NoticesStagesv2.ClosedTender:
    case NoticesStagesv2.CancelledTender:
    case NoticesStagesv2.UnknownTender: {
      const date = notice.closeDate;
      const isFutureDate = date ? new Date(date) > now : false;
      return {
        label: isFutureDate ? "Closes" : "Closed",
        date,
      };
    }

    default:
      return { label: "", date: "" };
  }
}

export function getPublishDateRangeLabel(fromPublishYear: number, toPublishYear: number) {
  if (fromPublishYear === toPublishYear) {
    return `published in ${fromPublishYear}`;
  }
  return `published between ${fromPublishYear} and ${toPublishYear}`;
}
